import type { SxProps, Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Typography } from "ui/typography";
import ControlBar from "components/partials/control-bar/control-bar";
import { ActionsMenu, ActionsMenuButton, ActionsMenuTrigger, MenuItem } from "ui/actions-menu";
import NewCreativeMenu from "containers/admin/clients/campaign/campaign-creative-page/components/new-creative-menu/new-creative-menu";
import RefineCreativesContentModal from "components/modals/refine-creatives-content-modal/refine-creatives-content-modal";
import { useRewriteCreativesWithWriter } from "components/modals/refine-creatives-content-modal/use-rewrite-creatives-with-writer";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";
import { getRewriteStrategyLinks } from "containers/admin/clients/campaign/rewrite-strategy-links";

import Icons from "assets";
import { useState, useMemo } from "react";
import { TouchpointType } from "types/touchpoint";
import { isLandingPageBuilderType } from "types/typeguards";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { useTouchpointActionContext } from "../../contexts/touchpoint-action-context";
import { useCreateFolderModal } from "../../hooks/modals/use-create-folder-modal";
import { useAddFromTemplateModal } from "../../hooks/modals/use-add-from-template-modal";
import { useAddCreativesWithWriterModal } from "../../hooks/modals/use-add-creatives-with-writer-modal";
import { useAddEmailModal } from "../../hooks/modals/use-add-email-modal";
import { useAddTouchpointModal } from "../../hooks/modals/use-add-touchpoint-modal";
import { isTouchpointInReview } from "utilities/touchpoint";

import { useFeatureFlag } from "contexts/feature-flag-context";

const ROOT_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

const SELECTED_COUNT_SX: SxProps<Theme> = (theme) => ({
  px: 0.75,
  fontWeight: 500,
  color: theme.palette.grey[900],
});

interface CampaignContextMenuProps {
  totalCreatives: number;
  selectedTouchpoints: TouchpointAttributes[];
  folderId?: string;
  newCreativeMenuPage?: "creative" | "folder";
  isMondayFolder?: boolean;
  hideNewCreativeMenu?: boolean;
}

const CampaignContextMenu = ({
  totalCreatives,
  selectedTouchpoints,
  folderId,
  newCreativeMenuPage = "creative",
  isMondayFolder,
  hideNewCreativeMenu = false,
}: CampaignContextMenuProps) => {
  const currentUser = useCurrentUser();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [isRewriteMenuOpen, setIsRewriteMenuOpen] = useState(false);
  const [isRefineCreativesModalOpen, setIsRefineCreativesModalOpen] = useState(false);
  const [rewriteTouchpointIds, setRewriteTouchpointIds] = useState<string[] | undefined>(undefined);

  const handleRewriteMenuOpen = () => setIsRewriteMenuOpen(true);
  const handleRewriteMenuClose = () => setIsRewriteMenuOpen(false);

  const handleActionsDropdownOpen = () => setIsActionsDropdownOpen(true);
  const handleActionsDropdownClose = () => setIsActionsDropdownOpen(false);

  const campaignHasStrategyData = currentCampaign.strategyData != null;

  const {
    openArchiveModal,
    openAddToFolderModal,
    openAddBatchModal,
    openRollToNextFYModal,
    openUpdateMergeTagsModal,
    openMoveToCampaignModal,
    onSuccess,
  } = useTouchpointActionContext();

  const { rewriteCreatives, isSubmitting: isRewriteCreativesSubmitting } = useRewriteCreativesWithWriter({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointIds: rewriteTouchpointIds,
    onSuccess: () => {
      setIsRefineCreativesModalOpen(false);
      // touchpointIds is only set when rewriting the current selection (Actions menu);
      // the 3-dot "rewrite all" entry point has nothing selected to clear.
      if (rewriteTouchpointIds) {
        onSuccess();
      }
    },
  });

  const rewriteStrategyLinks = getRewriteStrategyLinks(currentClient.id, currentCampaign);

  const { openCreateFolder, createFolderModalMarkup } = useCreateFolderModal();
  const { openAddFromTemplate, addFromTemplateModalMarkup } = useAddFromTemplateModal({
    folderId,
    onSuccess,
  });
  const { openAddCreativesWithWriter, addCreativesWithWriterModalMarkup } =
    useAddCreativesWithWriterModal({ folderId });
  const { openAddEmailModal, addEmailModalMarkup } = useAddEmailModal({ folderId });
  const { openAddTouchpointModal, addTouchpointModalMarkup } = useAddTouchpointModal({ folderId });

  const allSelectedAmount = selectedTouchpoints.length;

  const filteredByStatusList = useMemo(
    () => selectedTouchpoints.filter(isTouchpointInReview),
    [selectedTouchpoints],
  );

  const hasSelectedWithFolder = selectedTouchpoints.some((tp) => !!tp.folderId);
  const isAddToBatchDisabled = filteredByStatusList.length === 0;
  const isActionDisabled = selectedTouchpoints.length === 0;

  const hasTouchpointWritePermission = hasPermission(currentUser, Permission.PERM_TOUCHPOINT_WRITE);

  const addToFolderLabel = hasSelectedWithFolder ? "Move to folder" : "Add to folder";

  if (!hasTouchpointWritePermission) {
    return null;
  }

  return (
    <>
      <Box sx={ROOT_SX} data-testid="campaign-context-menu">
        {!!allSelectedAmount && (
          <ControlBar variant="inset" data-testid="actions-control-bar">
            <Typography component="span" variant="body2" sx={SELECTED_COUNT_SX}>
              Selected {allSelectedAmount} of {totalCreatives} items
            </Typography>

            <ActionsMenu
              itemName="selected creatives"
              open={isActionsDropdownOpen}
              offsetLeft={2}
              offsetTop={10}
              onOpen={handleActionsDropdownOpen}
              onClose={handleActionsDropdownClose}
              menuId="campaign-actions-menu"
              trigger={
                <ActionsMenuButton size="large" variant="outlined" data-testid="actions-button">
                  Actions
                </ActionsMenuButton>
              }>
              <Box data-testid="actions-menu-content">
                {campaignHasStrategyData && (
                  <MenuItem
                    icon={<Icons.CampaignWriter />}
                    label="Rewrite creatives"
                    onClick={() => {
                      setRewriteTouchpointIds(selectedTouchpoints.map((touchpoint) => touchpoint.id));
                      setIsActionsDropdownOpen(false);
                      setIsRefineCreativesModalOpen(true);
                    }}
                    disabled={isActionDisabled}
                    data-testid="actions-menu-rewrite-creatives"
                  />
                )}
                <MenuItem
                  icon={<Icons.Edit />}
                  label="Roll to next FY"
                  onClick={() => {
                    openRollToNextFYModal(selectedTouchpoints);
                    setIsActionsDropdownOpen(false);
                  }}
                  disabled={isActionDisabled}
                  data-testid="actions-menu-roll-to-next-fy"
                />
                <MenuItem
                  icon={<Icons.UpdateMergeTags />}
                  label="Update merge tags"
                  onClick={() => {
                    openUpdateMergeTagsModal(selectedTouchpoints);
                    setIsActionsDropdownOpen(false);
                  }}
                  disabled={isActionDisabled}
                  data-testid="actions-menu-update-merge-tags"
                />
                <MenuItem
                  icon={<Icons.Folder />}
                  label={addToFolderLabel}
                  onClick={() => {
                    openAddToFolderModal(selectedTouchpoints);
                    setIsActionsDropdownOpen(false);
                  }}
                  disabled={isActionDisabled}
                  data-testid="actions-menu-add-to-folder"
                />
                {isBatchManagementEnabled && (
                  <MenuItem
                    icon={<Icons.Layers />}
                    label="Add to batch"
                    onClick={() => {
                      openAddBatchModal(filteredByStatusList);
                      setIsActionsDropdownOpen(false);
                    }}
                    disabled={isAddToBatchDisabled}
                    tooltip={
                      isAddToBatchDisabled
                        ? "Please select at least one creative with status 'Not reviewed yet' or 'In review'"
                        : undefined
                    }
                    data-testid="actions-menu-add-to-batch"
                  />
                )}
                <MenuItem
                  icon={<Icons.SwapHorizontal />}
                  label="Move to campaign"
                  onClick={() => {
                    openMoveToCampaignModal(selectedTouchpoints);
                    setIsActionsDropdownOpen(false);
                  }}
                  disabled={isActionDisabled}
                  data-testid="actions-menu-move-to-campaign"
                />
                {hasPermission(currentUser, Permission.PERM_TOUCHPOINT_ADVANCED) && (
                  <MenuItem
                    icon={<Icons.Archive />}
                    label="Archive creatives"
                    color="error"
                    onClick={() => {
                      openArchiveModal(selectedTouchpoints);
                      setIsActionsDropdownOpen(false);
                    }}
                    disabled={isActionDisabled}
                    data-testid="actions-menu-archive"
                  />
                )}
              </Box>
            </ActionsMenu>
          </ControlBar>
        )}
        {!hideNewCreativeMenu && !allSelectedAmount && (
          <ControlBar variant="inset" data-testid="create-new-control-bar">
            <NewCreativeMenu
              page={newCreativeMenuPage}
              onCreateFolder={openCreateFolder}
              onAddFromTemplate={openAddFromTemplate}
              onAddCreativesWithWriter={openAddCreativesWithWriter}
              onOpenCreativeDetails={(type) => {
                if (type === TouchpointType.EMAIL) openAddEmailModal();
                if (isLandingPageBuilderType(type)) {
                  openAddTouchpointModal(type);
                }
              }}
              isMondayFolder={!!isMondayFolder}
            />
            {campaignHasStrategyData && !folderId && (
              <ActionsMenu
                itemName="rewrite creatives"
                open={isRewriteMenuOpen}
                onOpen={handleRewriteMenuOpen}
                onClose={handleRewriteMenuClose}
                menuId="rewrite-creatives-menu"
                offsetLeft={2}
                offsetTop={10}
                trigger={<ActionsMenuTrigger size="large" data-testid="rewrite-creatives-menu-button" />}>
                <MenuItem
                  icon={<Icons.CampaignWriter />}
                  label="Rewrite creatives"
                  onClick={() => {
                    setRewriteTouchpointIds(undefined);
                    setIsRewriteMenuOpen(false);
                    setIsRefineCreativesModalOpen(true);
                  }}
                  data-testid="rewrite-creatives-menu-with-writer"
                />
              </ActionsMenu>
            )}
          </ControlBar>
        )}
      </Box>
      {createFolderModalMarkup}
      {addFromTemplateModalMarkup}
      {addCreativesWithWriterModalMarkup}
      {addEmailModalMarkup}
      {addTouchpointModalMarkup}
      {isRefineCreativesModalOpen && (
        <RefineCreativesContentModal
          onCloseCallback={() => setIsRefineCreativesModalOpen(false)}
          onConfirmCallback={rewriteCreatives}
          isSubmitting={isRewriteCreativesSubmitting}
          {...rewriteStrategyLinks}
        />
      )}
    </>
  );
};

export default CampaignContextMenu;
